import {
  Page,
  Layout,
  Card,
  IndexTable,
  Text,
  Badge,
  Pagination,
  DatePicker,
  Popover,
  Button,
  Box,
  InlineStack,
} from "@shopify/polaris";
import { CalendarIcon } from "@shopify/polaris-icons";
import { useState, useEffect, useCallback } from "react";
import storageUtils from "../utils/storageUtils";
import { q_orders } from "../api";

const formatDateForQuery = (date) => {
  if (!date) return null;
  // 使用本地时间获取年、月、日，因为 DatePicker 是按本地时间让你选的
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function DashBoard() {
  const user_token = storageUtils.get("user_token");
  const [orderList, setOrderList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(20);

  const [selectedMonthYear, setSelectedMonthYear] = useState({
    month: new Date().getUTCMonth(),
    year: new Date().getUTCFullYear(),
  });

  const [selectedDates, setSelectedDates] = useState({
    start: new Date(new Date().setUTCDate(new Date().getUTCDate() - 7)),
    end: new Date(),
  });

  const [isDatePickerOpen, setDatePickerOpen] = useState(false);

  const queryOrderList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        user_token,
        page,
        limit,
        // 发送给后端的也是严格的 UTC 日期部分
        start_date: selectedDates.start
          ? formatDateForQuery(selectedDates.start)
          : "",
        end_date: selectedDates.end
          ? formatDateForQuery(selectedDates.end)
          : "",
      };

      const response = await q_orders(params);
      if (response.status === 200) {
        // 确保数据路径正确
        const list = response.data?.list || [];
        setOrderList(list);
        setTotalCount(response.data?.total || 0);
      }
    } catch (error) {
      console.error("Fetch orders failed:", error);
    } finally {
      setLoading(false);
    }
  }, [page, selectedDates, user_token, limit]);

  useEffect(() => {
    queryOrderList();
  }, [queryOrderList]);

  // 安全解析 JSON 函数
  const safeParse = (str) => {
    if (!str || typeof str !== "string" || str.trim() === "") return null;
    try {
      return JSON.parse(str);
    } catch (e) {
      return null;
    }
  };

  const rowMarkup = orderList.map((order, index) => {
    const {
      id,
      name,
      email,
      ip,
      status,
      create_time,
      shipping_address,
      line_items,
    } = order;

    // 表格内时间：严格提取 UTC 数值
    const d = new Date(create_time * 1000);
    const dateDisplay = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")} ${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")} UTC`;

    const parsedItems = safeParse(line_items) || [];
    const addr = safeParse(shipping_address);
    const productTitles =
      parsedItems.map((i) => i.product_title).join(", ") || "No items";
    const address1 = addr?.address_line1 || addr?.address1 || "";
    const address2 = addr?.address_line2 || addr?.address2 || "";

    return (
      <IndexTable.Row id={id.toString()} key={id} position={index}>
        <IndexTable.Cell>
          <Text fontWeight="bold" as="span">
            {name === "0" ? `D#${id}` : name}
          </Text>
        </IndexTable.Cell>
        <IndexTable.Cell>{email || "N/A"}</IndexTable.Cell>
        <IndexTable.Cell>
          <div
            style={{
              fontSize: "12px",
              maxWidth: "200px",
              whiteSpace: "normal",
            }}
          >
            {addr
              ? `${address1 ? `${address1}, ` : ""}${address2 ? `${address2}, ` : ""}${addr.city ? addr.city : ""}, ${addr.country}`
              : "No Address"}
          </div>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <div style={{ maxWidth: "200px", whiteSpace: "normal" }}>
            {productTitles}
          </div>
        </IndexTable.Cell>
        <IndexTable.Cell>{ip || "-"}</IndexTable.Cell>
        <IndexTable.Cell>{dateDisplay}</IndexTable.Cell>
        <IndexTable.Cell>
          <Badge tone={status === 1 ? "success" : "attention"}>
            {status === 1 ? "Paid" : "Pending"}
          </Badge>
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  return (
    <Page title="Orders Management (UTC Mode)" fullWidth>
      <Layout>
        <Layout.Section>
          <Box paddingBlockEnd="400">
            <InlineStack align="end" gap="200">
              <Popover
                active={isDatePickerOpen}
                activator={
                  <Button
                    onClick={() => setDatePickerOpen(!isDatePickerOpen)}
                    icon={CalendarIcon}
                  >
                    {selectedDates.start
                      ? `${formatDateForQuery(selectedDates.start)} - ${formatDateForQuery(selectedDates.end)}`
                      : "All Time"}
                  </Button>
                }
                onClose={() => setDatePickerOpen(false)}
              >
                <Box padding="400">
                  <DatePicker
                    month={selectedMonthYear.month}
                    year={selectedMonthYear.year}
                    onChange={(range) => {
                      setSelectedDates(range);
                      setPage(1);
                    }}
                    onMonthChange={(m, y) =>
                      setSelectedMonthYear({ month: m, year: y })
                    }
                    selected={selectedDates.start ? selectedDates : undefined}
                    allowRange
                    disableDatesAfter={new Date()}
                  />
                </Box>
              </Popover>
              <Button
                onClick={() => {
                  setSelectedDates({ start: null, end: null });
                  setPage(1);
                }}
                variant="tertiary"
              >
                Show All
              </Button>
            </InlineStack>
          </Box>
          <Card padding="0">
            <IndexTable
              resourceName={{ singular: "order", plural: "orders" }}
              itemCount={orderList.length}
              headings={[
                { title: "Order" },
                { title: "Customer" },
                { title: "Address" },
                { title: "Items" },
                { title: "IP" },
                { title: "Date (UTC)" },
                { title: "Status" },
              ]}
              selectable={false}
              loading={loading}
            >
              {rowMarkup}
            </IndexTable>
            <Box padding="400">
              <InlineStack align="center">
                <Pagination
                  hasPrevious={page > 1}
                  onPrevious={() => setPage(page - 1)}
                  hasNext={page < Math.ceil(totalCount / limit)}
                  onNext={() => setPage(page + 1)}
                  label={`Page ${page} of ${Math.ceil(totalCount / limit) || 1}`}
                />
              </InlineStack>
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
